import { router } from '@inertiajs/react'
import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu'
import { plan_top_down_path, plan_top_down_versioned_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

export interface VersionListItem {
  version_number: number
  published_at: string
  name: string | null
  note: string | null
  is_current: boolean
  // Set for a quarterly revision: months at the start of the plan already
  // covered by actual sales.
  actuals_months?: number | null
}

interface CurrentVersion {
  version_number: number | null
  published_at: string | null
  name: string | null
  is_draft: boolean
  actuals_months?: number | null
}

interface Props {
  slug: string
  versions: VersionListItem[]
  current: CurrentVersion | null
  readOnly: boolean
}

function formatVersionLabel(v: {
  version_number: number | null
  name: string | null
  is_draft?: boolean
  // null on a revision version that has not been published yet — distinct
  // from a draft (no version row at all).
  published_at?: string | null
  actuals_months?: number | null
}): string {
  let base: string
  if (v.is_draft || v.version_number == null) {
    base = v.name ? `Draft - ${v.name}` : 'Draft'
  } else if (v.published_at == null) {
    base = v.name ? `Revision in progress — ${v.name}` : 'Revision in progress'
  } else {
    base = v.name ? `v${v.version_number} - ${v.name}` : `v${v.version_number}`
  }
  return v.actuals_months ? `${base} · ${v.actuals_months} months actual` : base
}

function formatLastSaved(iso: string): string {
  const d = new Date(iso)
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
  return `Last saved ${date} ${time}`
}

function navigateToDraft(marketSlug: string, slug: string) {
  router.visit(plan_top_down_path(marketSlug, slug))
}

function navigateToVersion(marketSlug: string, slug: string, versionNumber: number) {
  window.open(plan_top_down_versioned_path(marketSlug, slug, String(versionNumber)), '_blank', 'noopener')
}

export default function VersionMenu({ slug, versions, current, readOnly }: Props) {
  const market = useMarket()
  const isOnDraft = current?.is_draft ?? false
  const currentPublished = versions.find(v => v.is_current) ?? null

  const triggerVersion =
    isOnDraft && currentPublished
      ? { version_number: currentPublished.version_number, name: currentPublished.name, published_at: currentPublished.published_at, is_draft: false }
      : current

  const statusLabel = readOnly ? 'read-only' : 'current'
  const triggerLine1 = triggerVersion
    ? `${formatVersionLabel(triggerVersion)} (${statusLabel})`
    : `Draft (${statusLabel})`
  const lastSaved = triggerVersion?.published_at ? formatLastSaved(triggerVersion.published_at) : null

  const displayedVersionNumber =
    isOnDraft && currentPublished ? currentPublished.version_number : current?.version_number ?? null

  if (versions.length === 0 && !current) return null

  const hasDraftOnly = isOnDraft && current && versions.length === 0
  const editableLabel = hasDraftOnly
    ? formatVersionLabel({ version_number: null, name: current?.name ?? null, is_draft: true })
    : currentPublished
      ? formatVersionLabel(currentPublished)
      : null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-auto gap-2 px-3 py-1.5 text-left focus-visible:ring-0 focus-visible:ring-offset-0">
          <span className="flex flex-col leading-tight">
            <span className="text-xs font-semibold text-foreground">{triggerLine1}</span>
            {lastSaved && <span className="text-[10px] text-muted-foreground">{lastSaved}</span>}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[16rem]">
        {editableLabel && (
          <>
            <DropdownMenuLabel>Only the latest current version is editable</DropdownMenuLabel>
            <DropdownMenuItem
              onSelect={() => navigateToDraft(market.slug, slug)}
              className="flex items-center justify-between gap-2"
            >
              <span className="text-xs">{editableLabel} (current)</span>
              {isOnDraft && <Check className="h-3.5 w-3.5 text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel>All recap versions of this plan</DropdownMenuLabel>
        {versions.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">No versions</div>
        ) : (
          versions.map(v => {
            const isDisplayed = !isOnDraft && v.version_number === displayedVersionNumber
            return (
              <DropdownMenuItem
                key={v.version_number}
                onSelect={() => navigateToVersion(market.slug, slug, v.version_number)}
                className="flex items-center justify-between gap-2"
              >
                <span className="text-xs">{formatVersionLabel(v)}</span>
                {isDisplayed && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenuItem>
            )
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
